import _ from 'lodash'
import Vue from 'vue'
import Vuex from 'vuex'
import auth from './modules/auth'
import init from './modules/init'
import pusher from './modules/pusher'
import form from './modules/form'
import datatable from './modules/datatable'
import datatableIndex from './modules/datatableIndex'
import datatableSettings from './modules/datatableSettings'
import companies from './modules/companies'
import metrics from './modules/metrics'
import properties from './modules/properties'
import accounts from './modules/accounts'
import notifications from './modules/notifications'
import flags from './modules/flags'
import account from './modules/account'
import users from './modules/users'
import settings from './modules/settings'
import subscriptions from './modules/subscriptions'
import activity from './modules/activity'
import search from './modules/search'
import invites from './modules/invites'
import roles from './modules/roles'
import fileDownloads from './modules/fileDownloads'
import moment from 'moment'

Vue.use(Vuex)

function initialState () {
  return {
    apiOnline: true,
    navIsExpanded: localStorage.getItem('navIsExpanded') || true,
    searchIsExpanded: false, // if the global search is open
    pageTitle: null,
    loading: false,
    twoFactorCheckNeeded: false,
    userSettings: {},
    modelUpdatedAt: Date(),
    dataChanged: '', // tracking which part of the data changed
    accountActiveTabs: JSON.parse(localStorage.getItem('accountActiveTabs')) || {}, // tracks which tab was open on each account page
    gmapsLoaded: false,
    datatableData: {},
    datatableState: {},
    underModalLink: null,
    filePreviewOpen: null,
    lastActivity: null,
    isTimeoutModalShown: false,
    // Modules
    auth: {
      authenticatedToken: null,
      refreshToken: null,
      status: ''
    },
    accounts: {
      recent: {},
      favorites: {},
      invited: {},
      statuses: [],
      statusCounts: {},
      initialized: false
    },
    metrics: {
      'all': {}
    },
    notifications: {
      all: [],
      count: 0
    },
    init: {
      all: false
    },
    accountInvite: null
  }
}

const store = new Vuex.Store({
  modules: {
    auth,
    init,
    pusher,
    form,
    datatable,
    datatableIndex,
    datatableSettings,
    accounts,
    notifications,
    flags,
    account,
    companies,
    metrics,
    properties,
    subscriptions,
    users,
    settings,
    activity,
    search,
    invites,
    roles,
    fileDownloads
  },
  state: initialState,
  getters: {
    init: state => state.init.all,
    userSettings: state => state.userSettings,
    modelUpdatedAt: state => state.modelUpdatedAt,
    dataChanged: state => state.dataChanged,
    accountInvite: state => state.accountInvite,
    userCan: (state) => (feature, type) => {
      if (state.init.all.accessible_features === undefined) return false
      return _.includes(state.init.all.accessible_features[type], feature)
    },
    userRole: (state) => (role) => {
      if (state.init.all.auth_user === undefined) return false
      return _.includes(state.init.all.auth_user.roles, role)
    },
    datatableSearch: (state) => (model) => {
      if (state.datatableState[model] === undefined) return ''
      if (state.datatableState[model].search === undefined) return ''

      return state.datatableState[model].search
    },
    datatableSortCol: (state) => (model) => {
      if (state.datatableState[model] === undefined) return ''
      if (state.datatableState[model].sortCol === undefined) return ''

      return state.datatableState[model].sortCol
    },
    datatableSortDir: (state) => (model) => {
      if (state.datatableState[model] === undefined) return ''
      if (state.datatableState[model].sortDir === undefined) return ''

      return state.datatableState[model].sortDir
    },
    datatableFilters: (state) => (model) => {
      if (state.datatableState[model] === undefined) return {}
      if (state.datatableState[model].filters === undefined) return {}

      return state.datatableState[model].filters
    },
    datatableSubTitle: (state) => (model) => {
      if (state.datatableState[model] === undefined) return ''
      if (state.datatableState[model].subtitle === undefined) return ''

      return state.datatableState[model].subtitle
    },
    isFromInviteLink: (state) => {
      if (Object.keys(state.init.all).length === 0) return false

      return state.init.all.auth_user.via_invite
    },
    twoFactorNeeded: (state) => {
      if (state.init.all.auth_user === undefined) return false
      return state.twoFactorCheckNeeded
    }
  },
  actions: {
    forceOpenSidebar ({ commit }) {
      commit('forceSidebarOpen')
    },
    setInvite ({ commit }, invite) {
      commit('setInviteData', invite)
    },
    updateLastActivity ({ commit }) {
      commit('updateLastActivity')
    },
    showTimeoutModal ({ commit }) {
      commit('showTimeoutModal')
    },
    hideTimeoutModal ({ commit }) {
      commit('hideTimeoutModal')
    },
    toggle2fa ({ commit }, checkRequired) {
      commit('toggle2fa', checkRequired)
    }
  },
  mutations: {
    setApiStatus (state, status) {
      state.apiOnline = status
    },
    resetState (state) {
      // https://github.com/vuejs/vuex/issues/1118
      const s = initialState()
      Object.keys(s).forEach(key => {
        state[key] = s[key]
      })

      localStorage.removeItem('refreshToken')
      localStorage.removeItem('authenticatedToken')
      localStorage.removeItem('fromShareLink')

      sessionStorage.removeItem('email')
    },
    // Open or close the navigation sidebar
    toggleSidebar (state) {
      state.navIsExpanded = !state.navIsExpanded
      localStorage.setItem('navIsExpanded', state.navIsExpanded)
    },
    forceSidebarOpen (state) {
      state.navIsExpanded = true
    },
    forceSidebarState (state, value) {
      state.navIsExpanded = value
    },
    // open or close the global search
    // also open the overlay to dim the background
    toggleGlobalSearch (state) {
      state.searchIsExpanded = !state.searchIsExpanded
    },
    setFilePreview (state, value) {
      state.filePreviewOpen = value
    },
    setPageTitle (state, title) {
      state.pageTitle = title
      if (title === undefined) title = 'loading...'
      document.title = title + ' - ' + process.env.NAME
    },
    setDocumentTitle (state, title) {
      document.title = title + ' - ' + process.env.NAME
    },
    popDocumentTitle (state) {
      document.title = document.title.substring(document.title.indexOf(' - ') + 3)
    },
    doneLoading (state) {
      setTimeout(function () {
        state.loading = false
      }, 250)
    },
    isLoading (state) {
      state.loading = true
    },
    setUnderModalLink (state, path) {
      // allow path reset to null
      // only set underModalLink if we don't have one set already
      if (path !== null && state.underModalLink !== null) return
      state.underModalLink = path
    },
    modelUpdated (state, what) {
      if (!what) what = 'data'

      // Used to refresh items on the page when updated from a modal
      state.modelUpdatedAt = Date()
      state.dataChanged = what // tracking what changed
    },
    datatableUpdated (state) {
      state.dataChanged = '' // tracking what changed
    },
    setDatatableSettings (state, payload) {
      if (state.userSettings.datatable === undefined) {
        state.userSettings = { 'datatable': {} }
      }

      if (state.userSettings.datatable[payload.key] === undefined) {
        state.userSettings.datatable[payload.key] = { }
      }

      // Watcher doesn't work on this because it's "deep"
      // Apparently watchers only watch the top level
      state.userSettings['datatable'][payload.key] = payload.settings
    },
    setDatatableData (state, payload) {
      Object.assign(state.datatableData, payload)
    },
    setAccountActiveTab (state, payload) {
      if (!payload.hasOwnProperty('account_id') || !payload.hasOwnProperty('tab')) return
      Vue.set(state.accountActiveTabs, payload.account_id, payload.tab)
      localStorage.setItem('accountActiveTabs', JSON.stringify(state.accountActiveTabs))
    },
    setGoogleMapsLoaded (state) {
      state.gmapsLoaded = true
    },
    setInviteData (state, invite) {
      state.accountInvite = {
        name: invite.full_name,
        company: invite.company,
        date: invite.date,
        logo_url: invite.logo_url
      }
    },
    toggle2fa (state, checkRequired) {
      state.twoFactorCheckNeeded = checkRequired
    },
    updateLastActivity (state) {
      state.lastActivity = moment()
    },
    showTimeoutModal (state) {
      state.isTimeoutModalShown = true
    },
    hideTimeoutModal (state) {
      state.isTimeoutModalShown = false
    },
    initDatatableState (state, model) {
      if (!model) return

      if (state.datatableState[model] === undefined) {
        Vue.set(state.datatableState, model, {
          'filters': {},
          'search': '',
          'sortCol': '',
          'sortDir': '',
          'subtitle': ''
        })
      }
    },
    resetDatatableState (state, model) {
      if (!model) return

      Vue.set(state.datatableState, model, {
        'filters': {},
        'search': '',
        'sort': 'desc',
        'subtitle': ''
      })
    },
    updateDatatableStateSearch (state, payload) {
      if (state.datatableState[payload.model] === undefined) return
      if (state.datatableState[payload.model].search === undefined) return

      Vue.set(state.datatableState, payload.model, {
        'search': payload.search,
        'sortCol': state.datatableState[payload.model].sortCol,
        'sortDir': state.datatableState[payload.model].sortDir,
        'filters': state.datatableState[payload.model].filters,
        'subtitle': state.datatableState[payload.model].subtitle
      })
    },
    updateDatatableStateFilters (state, payload) {
      if (state.datatableState[payload.model] === undefined) return
      if (state.datatableState[payload.model].filters === undefined) return

      Vue.set(state.datatableState, payload.model, {
        'search': state.datatableState[payload.model].search,
        'sortCol': state.datatableState[payload.model].sortCol,
        'sortDir': state.datatableState[payload.model].sortDir,
        'filters': payload.filters,
        'subtitle': state.datatableState[payload.model].subtitle
      })
    },
    updateDatatableSubTitle (state, payload) {
      if (state.datatableState[payload.model] === undefined) return
      if (state.datatableState[payload.model].subtitle === undefined) return

      Vue.set(state.datatableState, payload.model, {
        'search': state.datatableState[payload.model].search,
        'sortCol': state.datatableState[payload.model].sortCol,
        'sortDir': state.datatableState[payload.model].sortDir,
        'filters': state.datatableState[payload.model].filters,
        'subtitle': payload.subtitle
      })
    },
    updateDatatableSortCol (state, payload) {
      if (state.datatableState[payload.model] === undefined) return
      if (state.datatableState[payload.model].sortCol === undefined) return

      Vue.set(state.datatableState, payload.model, {
        'search': state.datatableState[payload.model].search,
        'sortCol': payload.sortCol,
        'sortDir': state.datatableState[payload.model].sortDir,
        'filters': state.datatableState[payload.model].filters,
        'subtitle': state.datatableState[payload.model].subtitle
      })
    },
    updateDatatableSortDir (state, payload) {
      if (state.datatableState[payload.model] === undefined) return
      if (state.datatableState[payload.model].sortDir === undefined) return

      Vue.set(state.datatableState, payload.model, {
        'search': state.datatableState[payload.model].search,
        'sortCol': state.datatableState[payload.model].sortCol,
        'sortDir': payload.sortDir,
        'filters': state.datatableState[payload.model].filters,
        'subtitle': state.datatableState[payload.model].subtitle
      })
    }
  }
})

export default store



// WEBPACK FOOTER //
// ./src/store/index.js